/**
 * PRD-037 Phase 6: MFA Verification Component
 * Task 6.2: MFA Frontend Components
 *
 * Modal for verifying MFA codes during login or sensitive operations
 */

import React, { useState, useEffect, useRef } from 'react';
import './MFA.css';

const MFAVerify = ({ onVerify, onCancel, title = 'Two-Factor Authentication' }) => {
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Auto-focus input when component mounts
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [useBackupCode]);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && onCancel && !loading) {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onCancel, loading]);

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError(null);

    // Auto-submit when 6 digits entered
    if (value.length === 6 && !useBackupCode) {
      handleSubmit(value);
    }
  };

  const handleBackupCodeChange = (e) => {
    // Allow alphanumeric and dashes for backup codes
    const value = e.target.value.toUpperCase().slice(0, 9);
    setBackupCode(value);
    setError(null);
  };

  const handleSubmit = async (submittedCode = null) => {
    const codeToVerify = submittedCode || (useBackupCode ? backupCode : code);

    if (!codeToVerify) {
      setError('Please enter a code');
      return;
    }

    if (!useBackupCode && codeToVerify.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await onVerify({
        token: useBackupCode ? null : codeToVerify,
        backupCode: useBackupCode ? codeToVerify : null,
      });

      if (!result.success) {
        setError(result.error || 'Verification failed');
        // Clear code on failure
        if (useBackupCode) {
          setBackupCode('');
        } else {
          setCode('');
        }
      }
    } catch (err) {
      setError(err.message || 'Verification failed');
      if (useBackupCode) {
        setBackupCode('');
      } else {
        setCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleSubmit();
    }
  };

  return (
    <div className="mfa-verify-overlay">
      <div className="mfa-verify-modal">
        <div className="mfa-verify-header">
          <div className="mfa-icon small">
            <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
            </svg>
          </div>
          <h3>{title}</h3>
        </div>

        <div className="mfa-verify-content">
          {!useBackupCode ? (
            <>
              <p className="mfa-description">
                Enter the 6-digit code from your authenticator app
              </p>

              <div className="code-input-container">
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={handleCodeChange}
                  onKeyDown={handleKeyDown}
                  placeholder="000000"
                  className="code-input"
                  disabled={loading}
                  autoComplete="one-time-code"
                />
              </div>

              <button
                className="btn-link"
                onClick={() => {
                  setUseBackupCode(true);
                  setCode('');
                  setError(null);
                }}
              >
                Use a backup code instead
              </button>
            </>
          ) : (
            <>
              <p className="mfa-description">
                Enter one of your backup codes
              </p>

              <div className="code-input-container">
                <input
                  ref={inputRef}
                  type="text"
                  value={backupCode}
                  onChange={handleBackupCodeChange}
                  onKeyDown={handleKeyDown}
                  placeholder="XXXX-XXXX"
                  className="code-input backup"
                  disabled={loading}
                />
              </div>

              <button
                className="btn-link"
                onClick={() => {
                  setUseBackupCode(false);
                  setBackupCode('');
                  setError(null);
                }}
              >
                Use authenticator app instead
              </button>
            </>
          )}

          {error && <div className="mfa-error">{error}</div>}

          <div className="mfa-actions">
            <button
              className="btn-primary"
              onClick={() => handleSubmit()}
              disabled={loading || (!useBackupCode && code.length !== 6) || (useBackupCode && !backupCode)}
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
            {onCancel && (
              <button className="btn-secondary" onClick={onCancel} disabled={loading}>
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFAVerify;
